// ============================================================================
//  AVATAR SELECTOR COMPONENT

// DiceBear avatar picker with multiple styles + custom upload
// ============================================================================

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Box, useToast, Spinner } from '@chakra-ui/react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';
import gameApiService from '../../services/gameApiService';
import '../../styles/Tutorial.css';

// ============================================================================
// PSS CHARACTER ROSTER
// ============================================================================
// Branded full-body characters, one locked style. Art is static SVG in the
// bundle; tiers and prices below are DISPLAY ONLY - the server catalog is
// authoritative and a purchase is priced there, never from the client.
//
// The stored photoURL is the absolute *_bust.svg (square head-and-shoulders):
// every avatar surface in the app is a circle with object-fit cover, and a
// full body in a circle crops to a torso.
// ============================================================================

const PSS_HOST = 'https://pokersweepstars.web.app';

interface PssAvatar { id: string; name: string; tier: string; price: number; }

const PSS_ROSTER_FALLBACK: PssAvatar[] = [
  { id: 'ace',      name: 'Ace',      tier: 'base',      price: 0 },
  { id: 'lux',      name: 'Lux',      tier: 'base',      price: 0 },
  { id: 'nova',     name: 'Nova',     tier: 'base',      price: 0 },
  { id: 'rio',      name: 'Rio',      tier: 'base',      price: 0 },
  { id: 'dash',     name: 'Dash',     tier: 'rare',      price: 2500 },
  { id: 'ivy',      name: 'Ivy',      tier: 'rare',      price: 2500 },
  { id: 'bluff',    name: 'Bluff',    tier: 'epic',      price: 10000 },
  { id: 'maverick', name: 'Maverick', tier: 'legendary', price: 50000 },
  { id: 'ribbit',   name: 'Ribbit',        tier: 'meme', price: 5000 },
  { id: 'moondog',  name: 'Moon Dog',      tier: 'meme', price: 5000 },
  { id: 'feels',    name: 'Feels',         tier: 'meme', price: 5000 },
  { id: 'jaw',      name: 'The Jaw',       tier: 'meme', price: 5000 },
  { id: 'stonks',   name: 'Stonks',        tier: 'meme', price: 5000 },
  { id: 'tilt',     name: 'Full Tilt',     tier: 'meme', price: 5000 },
  { id: 'fish',     name: 'The Fish',      tier: 'meme', price: 5000 },
  { id: 'diamond',  name: 'Diamond Hands', tier: 'meme', price: 5000 },
];

const PSS_TIER_STYLE: Record<string, { label: string; color: string }> = {
  base:      { label: 'BASE',      color: '#7FA0B0' },
  rare:      { label: 'RARE',      color: '#38BDF8' },
  epic:      { label: 'EPIC',      color: '#A855F7' },
  legendary: { label: 'LEGENDARY', color: '#FFD700' },
  meme:      { label: 'MEME',      color: '#22C55E' },
};

/** Full-body render, shown in the collection grid (local = instant). */
const pssBodyUrl = (id: string) => `/avatars/pss/${id}.svg`;
/** Square bust, the URL that becomes photoURL (absolute, loads anywhere). */
const pssBustUrl = (id: string) => `${PSS_HOST}/avatars/pss/${id}_bust.svg`;

// ============================================================================
// DICEBEAR AVATAR STYLES
// Free avatar styles from DiceBear API
// ============================================================================

export type DiceBearStyle = 
  | 'adventurer'      // Cartoon adventurer style
  | 'avataaars'       // Notion-style avatars
  | 'big-ears'        // Cute big-eared characters
  | 'bottts'          // Robot avatars
  | 'croodles'        // Hand-drawn doodles
  | 'fun-emoji'       // Fun emoji faces
  | 'lorelei'         // Elegant illustrated faces
  | 'micah'           // Simple cartoon faces
  | 'miniavs'         // Minimal avatars
  | 'open-peeps'      // Hand-drawn people
  | 'personas'        // Character personas
  | 'pixel-art'       // Pixel art style (great for poker theme!)
  | 'thumbs';         // Thumbs up/down characters

// Style display names and descriptions
const STYLE_INFO: Record<DiceBearStyle, { name: string; emoji: string }> = {
  'adventurer': { name: 'Adventure', emoji: '' },

  'avataaars': { name: 'Cartoon', emoji: '' },

  'big-ears': { name: 'Cute', emoji: '' },

  'bottts': { name: 'Robot', emoji: '' },

  'croodles': { name: 'Doodle', emoji: '' },

  'fun-emoji': { name: 'Emoji', emoji: '' },

  'lorelei': { name: 'Elegant', emoji: '' },

  'micah': { name: 'Simple', emoji: '' },

  'miniavs': { name: 'Mini', emoji: '' },

  'open-peeps': { name: 'Peeps', emoji: '' },

  'personas': { name: 'Persona', emoji: '' },

  'pixel-art': { name: 'Pixel', emoji: '' },

  'thumbs': { name: 'Thumbs', emoji: '' },

};

// Default styles to show (most popular/relevant for poker)
const DEFAULT_STYLES: DiceBearStyle[] = [
  'pixel-art',     // Great for gaming theme
  'avataaars',     // Clean and popular
  'bottts',        // Fun robot style
  'fun-emoji',     // Expressive
  'adventurer',    // Character style
  'lorelei',       // Elegant
];

// ============================================================================
// GENERATE DICEBEAR URL
// ============================================================================

const generateDiceBearUrl = (
  style: DiceBearStyle, 
  seed: string,
  options: {
    backgroundColor?: string;
    size?: number;
  } = {}
): string => {
  const { backgroundColor = 'transparent', size = 200 } = options;
  
  // DiceBear API v7 URL format
  const baseUrl = `https://api.dicebear.com/7.x/${style}/svg`;
  const params = new URLSearchParams({
    seed,
    backgroundColor,
    size: size.toString(),
  });
  
  return `${baseUrl}?${params.toString()}`;
};

// Generate multiple avatar options for a style
const generateAvatarOptions = (
  style: DiceBearStyle, 
  count: number = 8,
  baseSeed: string = ''
): string[] => {
  const urls: string[] = [];
  
  for (let i = 0; i < count; i++) {
    const seed = baseSeed ? `${baseSeed}-${i}` : `avatar-${Date.now()}-${i}-${Math.random().toString(36).substring(7)}`;
    urls.push(generateDiceBearUrl(style, seed));
  }
  
  return urls;
};

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface AvatarSelectorProps {
  currentAvatar?: string;
  userId: string;
  onAvatarSelect: (avatarUrl: string) => void;
  onClose?: () => void;
  showUpload?: boolean;
  maxFileSize?: number; // in MB
}

// ============================================================================
// AVATAR SELECTOR COMPONENT
// ============================================================================

const AvatarSelector: React.FC<AvatarSelectorProps> = ({
  currentAvatar,
  userId,
  onAvatarSelect,
  onClose,
  showUpload = true,
  maxFileSize = 2, // 2MB default
}) => {
  // 'pss' is the featured tab - the branded roster leads, DiceBear follows.
  const [selectedStyle, setSelectedStyle] = useState<DiceBearStyle | 'pss'>('pss');
  const [selectedAvatar, setSelectedAvatar] = useState<string>(currentAvatar || '');
  const [uploading, setUploading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // ── PSS roster state ──────────────────────────────────────────────────────
  const [pssRoster, setPssRoster] = useState<PssAvatar[]>(PSS_ROSTER_FALLBACK);
  const [pssOwned, setPssOwned] = useState<string[]>([]);
  const [pssBalance, setPssBalance] = useState<number | null>(null);
  const [pssLive, setPssLive] = useState(false);        // server answered
  const [pendingUnlock, setPendingUnlock] = useState<PssAvatar | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    gameApiService.avatarRoster().then(r => {
      if (cancelled || !r.success) return;
      if (r.roster?.length) setPssRoster(r.roster);
      setPssOwned(r.owned || []);
      setPssBalance(r.playCoins ?? null);
      setPssLive(true);
    });
    return () => { cancelled = true; };
  }, []);

  const pssIsUnlocked = (a: PssAvatar) => a.price === 0 || pssOwned.includes(a.id);

  const handlePssTap = (a: PssAvatar) => {
    if (pssIsUnlocked(a)) {
      setPendingUnlock(null);
      setSelectedAvatar(pssBustUrl(a.id));
      onAvatarSelect(pssBustUrl(a.id));
      return;
    }
    // Locked: purchasing needs the live catalog, so prices shown are real.
    if (!pssLive) {
      toast({ title: 'Connect to unlock', status: 'warning', duration: 2500,
        description: 'Could not reach the server to price this avatar. Try again in a moment.' });
      return;
    }
    setPendingUnlock(a);
  };

  const handlePssUnlock = async () => {
    if (!pendingUnlock || unlocking) return;
    setUnlocking(true);
    const r = await gameApiService.purchaseAvatar(pendingUnlock.id);
    setUnlocking(false);
    if (!r.success) {
      toast({ title: 'Unlock failed', description: r.error, status: 'error', duration: 3500 });
      return;
    }
    const id = pendingUnlock.id;
    setPssOwned(prev => prev.includes(id) ? prev : [...prev, id]);
    if (typeof r.balance === 'number') setPssBalance(r.balance);
    setPendingUnlock(null);
    setSelectedAvatar(pssBustUrl(id));
    onAvatarSelect(pssBustUrl(id));
    toast({
      title: r.alreadyOwned ? 'Already yours' : `${pendingUnlock.name} unlocked`,
      description: r.alreadyOwned ? 'This avatar was unlocked before - no charge.'
        : r.charged ? `${r.charged.toLocaleString()} PlayCoins` : undefined,
      status: 'success', duration: 3000,
    });
  };

  // Generate avatar options for current style
  const [avatarSeed, setAvatarSeed] = useState<string>(`${userId}-${Date.now()}`);
  
  const avatarOptions = useMemo(() => {
    return generateAvatarOptions(selectedStyle, 12, avatarSeed);
  }, [selectedStyle, avatarSeed]);

  // ============================================================================
  // HANDLE STYLE CHANGE
  // ============================================================================
  
  const handleStyleChange = (style: DiceBearStyle) => {
    setSelectedStyle(style);
    setAvatarSeed(`${userId}-${style}-${Date.now()}`);
  };

  // ============================================================================
  // REGENERATE AVATARS
  // ============================================================================
  
  const handleRegenerate = () => {
    setRegenerating(true);
    setAvatarSeed(`${userId}-${Date.now()}-${Math.random()}`);
    setTimeout(() => setRegenerating(false), 300);
  };

  // ============================================================================
  // HANDLE AVATAR SELECT
  // ============================================================================
  
  const handleSelectAvatar = (url: string) => {
    setSelectedAvatar(url);
    onAvatarSelect(url);
  };

  // ============================================================================
  // HANDLE FILE UPLOAD
  // ============================================================================
  
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file (PNG, JPG, etc.)',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxFileSize) {
      toast({
        title: 'File too large',
        description: `Please select an image under ${maxFileSize}MB`,
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setUploading(true);

    try {
      // Create a unique filename
      const fileExtension = file.name.split('.').pop() || 'png';
      const fileName = `avatars/${userId}/${Date.now()}.${fileExtension}`;
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          uploadedBy: userId,
          uploadedAt: new Date().toISOString(),
        },
      });

      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef);
      
      // Select the uploaded avatar
      setSelectedAvatar(downloadUrl);
      onAvatarSelect(downloadUrl);

      toast({
        title: 'Avatar uploaded!',

        description: 'Your custom avatar has been saved',
        status: 'success',
        duration: 2000,
      });

    } catch (error: any) {
      console.error('Avatar upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'Could not upload avatar',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [userId, maxFileSize, onAvatarSelect, toast]);

  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <div className="avatar-selector">
      {/* Style Tabs */}
      <div className="avatar-style-tabs">
        <button
          className={`avatar-style-tab ${selectedStyle === 'pss' ? 'active' : ''}`}
          onClick={() => { setSelectedStyle('pss'); setPendingUnlock(null); }}
          style={selectedStyle === 'pss'
            ? { borderColor: '#FFD700', color: '#FFD700' } : { color: '#FFD700' }}
        >
          ★ PSS
        </button>
        {DEFAULT_STYLES.map((style) => (
          <button
            key={style}
            className={`avatar-style-tab ${selectedStyle === style ? 'active' : ''}`}
            onClick={() => handleStyleChange(style)}
          >
            {STYLE_INFO[style].emoji} {STYLE_INFO[style].name}
          </button>
        ))}
      </div>

      {/* ── PSS character roster ── */}
      {selectedStyle === 'pss' && (
        <>
          <Box textAlign="center" mb={3} fontSize="11px" color="gray.400">
            PokerSweepStars originals · unlock with PlayCoins
            {pssBalance !== null && (
              <Box as="span" color="cyan.300" ml={2}>
                Balance: {pssBalance.toLocaleString()} PC
              </Box>
            )}
          </Box>
          <div className="avatar-grid">
            {pssRoster.map((a) => {
              const unlocked = pssIsUnlocked(a);
              const tier = PSS_TIER_STYLE[a.tier] || PSS_TIER_STYLE.base;
              const isSelected = selectedAvatar === pssBustUrl(a.id);
              return (
                <div
                  key={a.id}
                  className={`avatar-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => handlePssTap(a)}
                  style={{ position: 'relative', overflow: 'hidden' }}
                >
                  <img
                    src={pssBodyUrl(a.id)}
                    alt={a.name}
                    loading="lazy"
                    style={{
                      backgroundColor: '#0d2233',
                      objectFit: 'contain',
                      filter: unlocked ? undefined : 'brightness(0.35) saturate(0.5)',
                    }}
                  />
                  <span style={{
                    position: 'absolute', top: 4, left: 4,
                    fontSize: 7, fontWeight: 800, letterSpacing: '0.12em',
                    color: tier.color, background: 'rgba(3,9,14,0.75)',
                    padding: '2px 6px', borderRadius: 8,
                  }}>{tier.label}</span>
                  {!unlocked && (
                    <span style={{
                      position: 'absolute', bottom: 4, left: '50%',
                      transform: 'translateX(-50%)', whiteSpace: 'nowrap',
                      fontSize: 8, fontWeight: 700, color: '#E8F4F8',
                      background: 'rgba(3,9,14,0.85)', padding: '3px 8px',
                      borderRadius: 10, border: '1px solid #1B4055',
                    }}>🔒 {a.price.toLocaleString()}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Unlock confirm bar */}
          {pendingUnlock && (
            <Box mt={3} p={3} bg="rgba(255,215,0,0.07)" border="1px solid"
              borderColor="rgba(255,215,0,0.35)" borderRadius="lg"
              display="flex" alignItems="center" gap={3} justifyContent="space-between">
              <Box fontSize="12px" color="gray.200">
                Unlock <b>{pendingUnlock.name}</b>
                <Box as="span" color={(PSS_TIER_STYLE[pendingUnlock.tier] || PSS_TIER_STYLE.base).color} ml={1}>
                  ({(PSS_TIER_STYLE[pendingUnlock.tier] || PSS_TIER_STYLE.base).label.toLowerCase()})
                </Box>
                {' '}for <b style={{ color: '#FFD700' }}>{pendingUnlock.price.toLocaleString()} PC</b>?
              </Box>
              <Box display="flex" gap={2}>
                <button className="tutorial-btn secondary" style={{ padding: '6px 12px', fontSize: 9 }}
                  onClick={() => setPendingUnlock(null)} disabled={unlocking}>
                  CANCEL
                </button>
                <button className="tutorial-btn" style={{ padding: '6px 14px', fontSize: 9 }}
                  onClick={handlePssUnlock} disabled={unlocking}>
                  {unlocking ? <Spinner size="xs" /> : 'UNLOCK'}
                </button>
              </Box>
            </Box>
          )}
        </>
      )}

      {selectedStyle !== 'pss' && (
      <>
      {/* Regenerate Button */}
      <Box textAlign="center" mb={3}>
        <button
          className="tutorial-btn secondary"
          onClick={handleRegenerate}
          disabled={regenerating}
          style={{ 
            padding: '8px 16px', 
            fontSize: '9px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {regenerating ? <Spinner size="xs" /> : ''} NEW AVATARS

        </button>
      </Box>

      {/* Avatar Grid */}
      <div className="avatar-grid">
        {/* Upload Option (first tile) */}
        {showUpload && (
          <div
            className={`avatar-option avatar-upload-btn ${selectedAvatar && !selectedAvatar.includes('dicebear') ? 'selected' : ''}`}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <Spinner color="cyan.400" />
            ) : (
              <>
                <span></span>

                <span className="avatar-upload-text">UPLOAD</span>
              </>
            )}
          </div>
        )}

        {/* DiceBear Avatar Options */}
        {avatarOptions.map((url, index) => (
          <div
            key={`${selectedStyle}-${index}`}
            className={`avatar-option ${selectedAvatar === url ? 'selected' : ''}`}
            onClick={() => handleSelectAvatar(url)}
          >
            <img 
              src={url} 
              alt={`Avatar option ${index + 1}`}
              loading="lazy"
              style={{
                backgroundColor: '#1a1a2e',
              }}
            />
          </div>
        ))}
      </div>
      </>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Current Selection Preview */}
      {selectedAvatar && (
        <Box 
          mt={4} 
          p={4} 
          bg="rgba(0, 217, 217, 0.1)" 
          borderRadius="lg"
          border="2px solid"
          borderColor="cyan.700"
          textAlign="center"
        >
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center"
            gap={3}
          >
            <img 
              src={selectedAvatar} 
              alt="Selected avatar"
              style={{
                width: 60,
                height: 60,
                borderRadius: 8,
                backgroundColor: '#1a1a2e',
                border: '2px solid #00D9D9',
              }}
            />
            <Box textAlign="left">
              <Box 
                fontFamily="'Press Start 2P', cursive"
                fontSize="8px"
                color="cyan.400"
                mb={1}
              >
                 SELECTED

              </Box>
              <Box 
                fontSize="10px"
                color="gray.400"
              >
                This will be your avatar
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default AvatarSelector;

// ============================================================================
// AVATAR DISPLAY COMPONENT (for showing avatars elsewhere)
// ============================================================================

interface AvatarDisplayProps {
  src?: string;
  userId: string;
  username?: string;
  size?: number;
  showBorder?: boolean;
  onClick?: () => void;
}

export const AvatarDisplay: React.FC<AvatarDisplayProps> = ({
  src,
  userId,
  username,
  size = 48,
  showBorder = true,
  onClick,
}) => {
  // Generate default avatar if no src provided
  const avatarUrl = src || generateDiceBearUrl('pixel-art', userId);
  
  // Fallback to initials if image fails
  const [imgError, setImgError] = useState(false);
  
  const getInitials = () => {
    if (username) {
      return username.substring(0, 2).toUpperCase();
    }
    return userId.substring(0, 2).toUpperCase();
  };

  return (
    <Box
      width={`${size}px`}
      height={`${size}px`}
      borderRadius="full"
      overflow="hidden"
      border={showBorder ? '3px solid' : 'none'}
      borderColor="cyan.500"
      bg="gray.800"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      transition="all 0.2s"
      _hover={onClick ? { 
        transform: 'scale(1.05)',
        borderColor: 'cyan.400',
        boxShadow: '0 0 15px rgba(0, 217, 217, 0.4)',
      } : undefined}
    >
      {imgError ? (
        <Box
          fontSize={`${size * 0.4}px`}
          fontWeight="bold"
          color="cyan.400"
          fontFamily="'Press Start 2P', cursive"
        >
          {getInitials()}
        </Box>
      ) : (
        <img
          src={avatarUrl}
          alt={username || 'User avatar'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onError={() => setImgError(true)}
        />
      )}
    </Box>
  );
};

// ============================================================================
// UTILITY: Generate avatar URL for a user
// ============================================================================

export const getDefaultAvatarUrl = (userId: string, style: DiceBearStyle = 'pixel-art'): string => {
  return generateDiceBearUrl(style, userId);
};